/**
 * REFERENCE — the in-app guide, and the design system it is written in.
 *
 * The guide is in the product rather than beside it. The UX audits' recurring
 * finding was that a cold user cannot work out the model from the surfaces, and a
 * documentation site does not fix that for someone already inside the app with a
 * finding open. Everything here is one click from the rail.
 *
 * The standalone HTML handbook (`/styleguide/verification-handbook.html`) is the
 * longer-form twin of this page, for someone who has not opened the product at all.
 */

import { GUIDE_DOC_URL, NAV, href } from "./router";
import {
  CONTROLS,
  METRICS,
  PACKS,
  RECEIPTS,
  RUNS,
  SOURCES,
} from "./data";
import {
  Badge,
  Button,
  Callout,
  Card,
  Grid,
  Icon,
  PageShell,
  Pipeline,
  Section,
  Stat,
  Table,
  Verdict,
} from "./ui";

/* ══════════════════════════════════════════════════════════════════════════
   THE GUIDE
   ══════════════════════════════════════════════════════════════════════════ */

interface Job {
  persona: string;
  when: string;
  want: string;
  so: string;
  surface: string;
  path: string;
}

/**
 * Jobs to be done, per persona.
 *
 * Taken from the strategy and UX docs rather than invented: each one maps to a
 * real surface, and the mapping is the test of whether the IA is complete. A job
 * with no surface is a gap; a surface serving no job is decoration. Both are worth
 * seeing in one table.
 *
 * ORDER IS LOAD-BEARING. `GuidePage` derives its persona sections from the order
 * personas first appear here, so this array decides which persona a reader meets
 * first. The engineer bringing a workflow leads, because that is the capability
 * the product is built around — a framework control is the same problem with a
 * framework's name on it, not the other way round. Pinned by
 * `pnpm test-verification-positioning`, which reads this array rather than the
 * JSX so the assertion cannot be satisfied by decoration.
 */
const JOBS: Job[] = [
  {
    persona: "Engineer / agent integrator",
    when: "my agent does something I cannot take its word for",
    want: "to register it with an independent oracle that can settle it without the agent",
    so: "the outcome is verified rather than self-reported",
    surface: "Workflows",
    path: "workflows",
  },
  {
    persona: "Engineer / agent integrator",
    when: "my workflow is admitted and running",
    want: "a run id, a route and a bundle hash I can store on my own record",
    so: "my system can point at proof that does not depend on its own logs",
    surface: "API & SDK",
    path: "api",
  },
  {
    persona: "Engineer / agent integrator",
    when: "I need a verdict to be trustworthy outside this product",
    want: "to replay the evidence pack offline and get the same answer",
    so: "the proof does not rest on trusting Excellent's servers or good faith",
    surface: "Receipts",
    path: "receipts",
  },
  {
    persona: "Compliance owner (the buyer)",
    when: "our SOC 2 window is closing and I do not know what we can actually prove",
    want: "one screen that tells me which controls carry real evidence and which do not",
    so: "I find the gaps before an auditor does, while there is still time to fix them",
    surface: "Overview",
    path: "",
  },
  {
    persona: "Compliance owner (the buyer)",
    when: "a control comes back FAIL or UNKNOWN",
    want: "to assign it, set a due date, record what was done, and re-verify",
    so: "the fix is provable rather than asserted, and the receipt shows it closed",
    surface: "Findings",
    path: "findings",
  },
  {
    persona: "Compliance owner (the buyer)",
    when: "an auditor asks for evidence",
    want: "a scoped bundle that states its own limitations and verifies without me",
    so: "I hand over exactly what is true, and answer far fewer follow-up questions",
    surface: "Evidence packs",
    path: "packs",
  },
  {
    persona: "Reviewer (the daily user)",
    when: "I sit down to work through the queue",
    want: "the worst thing first, with the question I am being asked stated plainly",
    so: "I spend my attention deciding rather than working out what I am looking at",
    surface: "Decision inbox",
    path: "queue",
  },
  {
    persona: "Reviewer (the daily user)",
    when: "I disagree with the control owner about whether something is acceptable",
    want: "to record my reasoning and escalate rather than settle it alone",
    so: "the disagreement is on the record instead of being resolved by whoever is more insistent",
    surface: "Finding detail",
    path: "findings/fnd_4403",
  },
  {
    persona: "Reviewer (the daily user)",
    when: "someone asks me six months later why we accepted something",
    want: "the claim, the checks, the evidence and every decision in one readable page",
    so: "I can explain it without reading raw JSON or trusting my memory",
    surface: "Run detail",
    path: "runs/run_8818",
  },
  {
    persona: "External auditor",
    when: "I am given access to a client's evidence",
    want: "read-only, time-limited access to scoped packs I can reproduce myself",
    so: "I can test the determination independently rather than taking it on faith",
    surface: "Audit room",
    path: "audit-room",
  },
  {
    persona: "Operator",
    when: "a control has gone UNKNOWN and I do not know why",
    want: "source health, scopes and freshness with the affected control named",
    so: "I fix the cause instead of triaging the symptom",
    surface: "Connectors",
    path: "connectors",
  },
  {
    persona: "Operator",
    when: "we are deciding whether to make a commercial claim about this",
    want: "the nine metrics that measure the verification's own trustworthiness",
    so: "the claim is backed by measurement rather than by confidence",
    surface: "Scorecard",
    path: "scorecard",
  },
  {
    persona: "Security reviewer (at the buyer)",
    when: "you are asking for credentials to our identity provider and cloud account",
    want: "the scopes, the boundary, and who holds the signing key",
    so: "I can approve this without a three-week questionnaire",
    surface: "Runtime & keys",
    path: "runtime",
  },
];

const VOCAB: { term: string; def: string }[] = [
  {
    term: "Claim",
    def: "What someone or something asserts happened — \"this refund was issued\", \"every account has MFA\". A claim is the input to verification, never its output.",
  },
  {
    term: "Oracle",
    def: "An independent source of truth the actor does not control. The one non-negotiable requirement: no oracle, no admission. An agent's own report is not an oracle.",
  },
  {
    term: "Control",
    def: "A thing that must be true, usually drawn from a framework — SOC 2 CC6.1, say. It has an owner, a frequency, a population, and a consequence if it fails.",
  },
  {
    term: "Requirement",
    def: "One checkable component of a control. A control with three requirements needs all three to pass before it passes.",
  },
  {
    term: "Evidence artifact",
    def: "Content-addressed source data — an export, a snapshot, a log. Identified by its SHA-256 and encrypted at rest. The store holds that metadata and an append-only lineage, not the payload, so the hash names the bytes observed rather than proving they are immutable.",
  },
  {
    term: "Observation",
    def: "A derived fact extracted from an artifact: \"user 00u4a9 has 0 MFA factors\". Checks read observations, not raw bytes, and the extractor is versioned.",
  },
  {
    term: "Policy",
    def: "The versioned, checkable logic that decides a control from its observations. Tightening it is routine. Loosening it requires impact analysis and a second person.",
  },
  {
    term: "Check result",
    def: "One requirement evaluated against evidence: a status, an authority, the observations it read, and the specific rows it cites.",
  },
  {
    term: "Authority",
    def: "Who decided: deterministic (a rule), semantic (a model), human, or imported. Only the first, third and fourth can support a receipt.",
  },
  {
    term: "PASS / FAIL / UNKNOWN / NOT RUN",
    def: "The four outcomes. UNKNOWN means the evidence cannot decide — it is a real answer, not a soft failure, and it blocks a receipt exactly as firmly as FAIL.",
  },
  {
    term: "Finding",
    def: "A FAIL or UNKNOWN surfaced for a human decision, carrying a severity, an owner and a due date. A PASS never opens one.",
  },
  {
    term: "Review decision",
    def: "A human outcome on a finding — accept as exception, request evidence, approve or reject a remediation, dismiss — always with a written rationale.",
  },
  {
    term: "Receipt",
    def: "The signed record binding a verdict to the exact evidence hashes, policy versions, engine build and decisions behind it. Append-only: corrections issue a linked child receipt.",
  },
  {
    term: "Run",
    def: "One execution of policies over evidence for a set of controls. The unit that produces receipts.",
  },
  {
    term: "Evidence pack",
    def: "A scoped, signed export containing receipts, the evidence they cite, the offline verifier, and a plain statement of what it does not cover.",
  },
  {
    term: "Audit room",
    def: "Time-limited read-only access for an external reviewer, scoped to specific packs, with every view and download appended to a hash chain.",
  },
  {
    term: "Semantic worker",
    def: "A model-based checker for evidence a rule cannot parse. It returns cited judgments and can never issue an authoritative PASS.",
  },
  {
    term: "Frozen case",
    def: "A historical situation with a known-correct answer, used as a regression test on policy changes. A change that flips one is a behaviour change, not a refinement.",
  },
  {
    term: "Tenant",
    def: "One customer workspace, with its own framework, observation window, runtime mode and key custody. No query crosses a tenant boundary.",
  },
];

export function GuidePage({ onNavigate }: { onNavigate: (to: string) => void }) {
  const personas = Array.from(new Set(JOBS.map((j) => j.persona)));

  return (
    <PageShell
      wide
      eyebrow="Reference"
        title="How this product works"
        lede="What it does, who it is for, the loop it runs, and the vocabulary it uses. Written for someone with a finding already open — the long-form version is the handbook."
        actions={
          <Button variant="primary" icon="external" onClick={() => window.open(GUIDE_DOC_URL, "_blank")}>
            Open the handbook
          </Button>
        }
    >

      {/*
        ── The lead ────────────────────────────────────────────────────────────
        This card comes first on purpose, and the marker below is what makes
        "first" checkable. The product's distinctive capability is verifying a
        workflow against a source its actor does not control; a framework control
        is one instance of that, and leading with the framework made the whole
        thing read as readiness software (gauntlet blocker F1). Pinned by
        `pnpm test-verification-positioning`, which asserts this marker precedes
        the page's first mention of compliance.
      */}
      <div data-positioning="workflow-first">
        <Card title="Start with a workflow you cannot take the word of">
          <p className="vp-prose" style={{ margin: "0" }}>
            An agent issues a refund and writes its own log line saying it worked. Someone confirms a deletion
            request by replying "done". A bot grants access and is the only record that it did. In each case the
            thing asserting the outcome is the thing being asked about. Excellent asks one question first —{" "}
            <strong>what would prove this, independently of whoever did the work?</strong> — then connects
            read-only to whatever holds that proof, evaluates versioned rules against what it finds, and routes
            everything it cannot settle mechanically to a person. Name no independent source and the workflow is
            not admitted; it produces nothing rather than producing reassurance.
          </p>
        </Card>
      </div>

      {/* ── One paragraph ─────────────────────────────────────────────────── */}
      <Card title="In one paragraph">
        <p className="vp-prose" style={{ margin: "0" }}>
          The output is a <strong>signed receipt</strong> binding each verdict to the exact evidence behind it,
          and an <strong>evidence pack</strong> a third party can verify offline with us switched off. A
          compliance requirement is the same shape of problem with a framework's name attached — the same
          intake, the same evaluation, the same receipt — so a control is something this serves rather than
          something it is built around. The product's distinguishing property is what it refuses to do: it will
          not pass a check on a model's opinion, it will not let a missing source read as a passing check, and it
          will not export a pack that implies coverage the evidence does not support.
        </p>
      </Card>

      {/* ── The loop ─────────────────────────────────────────────────────── */}
      <Section title="The loop" note="Six steps. Every one is a surface in the rail.">
        <Card flush>
          <Pipeline
            stages={[
              { name: "Connect", desc: "Name the claim and the independent oracle that can settle it." },
              { name: "Collect", desc: "Read-only connectors snapshot the source of truth, content-addressed." },
              { name: "Evaluate", desc: "Versioned policy runs against the observations. Deterministic first." },
              { name: "Decide", desc: "Every FAIL and UNKNOWN goes to a human with a required rationale." },
              { name: "Issue", desc: "A receipt binds the verdict to evidence, policy, engine and key." },
              { name: "Prove", desc: "A pack exports it, states its limits, and replays offline." },
            ]}
          />
        </Card>
      </Section>

      {/* ── Jobs to be done ──────────────────────────────────────────────── */}
      <Section
        title="Jobs to be done"
        note="Thirteen jobs across six personas, each mapped to the surface that serves it. A job with no surface is a gap; a surface serving no job is decoration."
      >
        {personas.map((p) => (
          <Card key={p} title={p} flush>
            <div className="vp-rows">
              {JOBS.filter((j) => j.persona === p).map((j, i) => (
                <button
                  key={i}
                  type="button"
                  className="vp-row"
                  data-clickable="true"
                  onClick={() => onNavigate(href(...(j.path ? [j.path] : [])))}
                  style={{ alignItems: "flex-start" }}
                >
                  <span className="vp-row-main">
                    <span className="vp-row-title" style={{ fontWeight: 400, lineHeight: 1.55 }}>
                      <strong style={{ color: "var(--ink-3)", fontWeight: 600 }}>When</strong> {j.when},{" "}
                      <strong style={{ color: "var(--ink-3)", fontWeight: 600 }}>I want</strong> {j.want},{" "}
                      <strong style={{ color: "var(--ink-3)", fontWeight: 600 }}>so that</strong> {j.so}.
                    </span>
                  </span>
                  <span className="vp-row-aside">
                    <Badge tone="accent">{j.surface}</Badge>
                  </span>
                </button>
              ))}
            </div>
          </Card>
        ))}
      </Section>

      {/* ── Information architecture ─────────────────────────────────────── */}
      <Section
        title="Page hierarchy"
        note="Seven groups, eighteen pages, eight object detail pages. The grouping is the loop's phases in causal order, with the daily job pulled to the top."
      >
        <div className="vp-stack" data-gap="sm">
          {NAV.map((g, gi) => (
            <Card
              key={g.label ?? `g${gi}`}
              title={g.label ?? "Top level"}
              subtitle={g.rationale}
              flush
            >
              <div className="vp-rows">
                {g.items.map((item) => (
                  <button
                    key={item.page}
                    type="button"
                    className="vp-row"
                    data-clickable="true"
                    onClick={() => onNavigate(href(...(item.path ? [item.path] : [])))}
                    style={{ alignItems: "flex-start" }}
                  >
                    <span style={{ flex: "0 0 auto", color: "var(--ink-3)", paddingTop: "calc(var(--spacing) * 0.5)" }}>
                      <Icon name={item.icon} size={15} />
                    </span>
                    <span className="vp-row-main">
                      <span className="vp-row-title">{item.label}</span>
                      <span className="vp-row-sub">{item.purpose}</span>
                      {item.alsoActiveFor && item.alsoActiveFor.length > 0 ? (
                        <span className="vp-row-sub vp-mono" style={{ fontSize: "var(--type-subtext-size)" }}>
                          detail page: /{item.path}/&lt;id&gt;
                        </span>
                      ) : null}
                    </span>
                    <span className="vp-row-aside">
                      <span className="vp-mono vp-tiny">/{item.path || ""}</span>
                    </span>
                  </button>
                ))}
              </div>
            </Card>
          ))}
        </div>
      </Section>

      {/* ── Vocabulary ───────────────────────────────────────────────────── */}
      <Section title="Vocabulary" note="Nineteen terms of art. These are the product's real names for things, matching what the engine emits.">
        <Card>
          <div className="vp-defs">
            {VOCAB.map((v) => (
              <div key={v.term} className="vp-def">
                <div className="vp-def-term">{v.term}</div>
                <div className="vp-def-body">{v.def}</div>
              </div>
            ))}
          </div>
        </Card>
      </Section>

      {/* ── The four rules ───────────────────────────────────────────────── */}
      <Section
        title="Four rules that cannot be configured away"
        note="Each one exists because its absence would make a receipt worthless. They are enforced in the type system and the engine, not in the UI."
      >
        <Grid cols={2}>
          <Card title="1 — No oracle, no admission">
            <p style={{ margin: "0", fontSize: "var(--type-body-size)", lineHeight: "var(--type-prose-leading)", color: "var(--ink-2)" }}>
              A workflow whose only evidence is the actor's own account of itself stays in{" "}
              <span className="vp-mono">needs-oracle</span> forever. Without this, the checks read evidence the
              actor controls, the verdict is circular, and the signature — cryptographically perfect — attests
              to nothing.
            </p>
          </Card>
          <Card title="2 — A model may not pass a control">
            <p style={{ margin: "0", fontSize: "var(--type-body-size)", lineHeight: "var(--type-prose-leading)", color: "var(--ink-2)" }}>
              A semantic worker's <span className="vp-mono">authoritativeStatus</span> is typed to accept only{" "}
              <span className="vp-mono">FAIL</span> or <span className="vp-mono">UNKNOWN</span>. A "support"
              judgment routes to human review. Not a setting — the shape of the type.
            </p>
          </Card>
          <Card title="3 — No decision without a rationale">
            <p style={{ margin: "0", fontSize: "var(--type-body-size)", lineHeight: "var(--type-prose-leading)", color: "var(--ink-2)" }}>
              The review form has no path that closes a finding with an empty reason.{" "}
              <span className="vp-mono">silent_approve_rate</span> is a hard zero on the scorecard rather than a
              target, because an unexplained approval is indistinguishable from a rubber stamp.
            </p>
          </Card>
          <Card title="4 — Loosening a rule needs a second person">
            <p style={{ margin: "0", fontSize: "var(--type-body-size)", lineHeight: "var(--type-prose-leading)", color: "var(--ink-2)" }}>
              A policy change that would flip a frozen case from FAIL to PASS requires impact analysis and
              activation by someone other than its author. Otherwise a failing control could be fixed by
              editing the test.
            </p>
          </Card>
        </Grid>
      </Section>

      {/* ── Built vs planned ─────────────────────────────────────────────── */}
      <Section
        title="What is real today"
        note="This prototype shows the whole product. The underlying system does not implement all of it yet, and the honest split is below."
      >
        <Grid cols={2}>
          <Card title="Built in code today" flush>
            <div className="vp-rows">
              {[
                "The canonical domain model — tenant, actor, control, requirement, artifact, observation, policy, check, finding, decision, receipt, run, export pack",
                "The deterministic SOC 2 kernel: policy AST build, validate, hash, evaluate, replay",
                "Receipt signing, canonicalisation and offline replay-manifest verification",
                "Content-addressed evidence store with append-only observations and hash-chained lineage",
                "The review case state machine, all eight states and seven transitions",
                "Policy studio admission — tighten/loosen classification and frozen-case impact analysis",
                "Six read-only native connector adapters with required and forbidden scopes",
                "Semantic worker orchestration, provider planning, and the never-PASS constraint",
                "Tenant boundary RBAC, the audit room's scoped grants and its append-only access log",
                "The 14-endpoint public API catalogue, token scopes, and the SDK request planner",
                "The nine-metric scorecard with its pass/warn/fail thresholds",
              ].map((t) => (
                <div key={t} className="vp-row" style={{ alignItems: "flex-start" }}>
                  <span style={{ flex: "0 0 auto", color: "var(--pass)", paddingTop: "calc(var(--spacing) * 0.5)" }}>
                    <Icon name="check" size={15} />
                  </span>
                  <span className="vp-row-main">
                    <span className="vp-row-title" style={{ fontWeight: 400, lineHeight: 1.5 }}>
                      {t}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </Card>

          <Card title="Not built, or not what it looks like" flush>
            <div className="vp-rows">
              {[
                "Continuous collection as a durable background queue — execution is still synchronous and API-driven, so the cadence and next-run times shown here are readiness state, not a running scheduler",
                "Raw evidence object storage — artifacts are package-level metadata today rather than durable file storage",
                "The Tier-3/Tier-4 judged scorer — the semantic half is specified and constrained but the distilled judge is not built",
                "Live model calls in semantic checks — no live provider authority is claimed",
                "Private runtime as a shipped customer deployment — the DigitalOcean provisioning path has been smoke-tested once, which is not the same as a customer running it",
                "The SDK as an installable runtime client — it is a request planner; the name oversells it",
                "Design-partner validation — closed by a human owner override, explicitly not machine-proven",
                "SOC 2 certification or an audit opinion — never claimed, and the product copy is careful about it",
              ].map((t) => (
                <div key={t} className="vp-row" style={{ alignItems: "flex-start" }}>
                  <span style={{ flex: "0 0 auto", color: "var(--unknown)", paddingTop: "calc(var(--spacing) * 0.5)" }}>
                    <Icon name="alert" size={15} />
                  </span>
                  <span className="vp-row-main">
                    <span className="vp-row-title" style={{ fontWeight: 400, lineHeight: 1.5 }}>
                      {t}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </Card>
        </Grid>
      </Section>

      <Section title="This prototype's own numbers" note="What the fixture workspace contains, so you know the scale the screens were designed against.">
        <Grid cols={4}>
          <Stat label="Controls" value={CONTROLS.length} foot="Across six SOC 2 families" />
          <Stat label="Sources" value={SOURCES.length} foot="Two deliberately broken" />
          <Stat label="Runs / receipts" value={`${RUNS.length} / ${RECEIPTS.length}`} foot="Including one correction receipt" />
          {/* The endpoint count used to ride along here, off a 14-row fixture that was a
              retype of the real 32-operation catalogue. The API is not part of this
              prototype's fixture scale, and this section is only about fixture scale —
              so it is gone rather than restated. The live catalogue is on `/api`, which
              derives it from `describeVerificationApiSurface()`. */}
          <Stat label="Packs" value={PACKS.length} foot="One shared with an external auditor" />
        </Grid>
      </Section>

      <div style={{ marginTop: "calc(var(--spacing) * 5)" }}>
        <Callout tone="waived" icon="info" title="This is a design prototype.">
          Every screen runs on a frozen fixture workspace, not a live database. Decisions you record here are
          real within the session — the queue count drops, timelines gain entries, control verdicts move — and
          reset on reload. The <strong>{METRICS.length} scorecard metrics</strong>, all status literals, and the
          object model are taken from the real engine so the design can be built as drawn.
        </Callout>
      </div>
    </PageShell>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   DESIGN SYSTEM
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * The design system page.
 *
 * A styleguide route that does not document its own system is a missed
 * opportunity, and more practically: if this proposal is accepted, this page is the
 * spec for extending the real atoms in `packages/ui/src/`. So the tokens, the
 * contrast ratios and the rules are all here in one readable place, with the
 * reasoning attached to each.
 */
/**
 * WCAG 2.x relative luminance and contrast ratio.
 *
 * The `/design` table used to carry HAND-TYPED ratios, and six of the ten were
 * wrong — all six overstated, by up to 0.62. That is the predictable outcome of
 * publishing measurements as string literals: a token gets nudged and the number
 * beside it does not. Computing them from the hex values means the table cannot
 * drift from the palette, and a token change shows its real cost immediately.
 */
function luminance(hex: string): number {
  const h = hex.replace("#", "");
  const channel = (i: number) => {
    const v = parseInt(h.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
}

function contrast(fg: string, hexBg: string): number {
  const a = luminance(fg);
  const b = luminance(hexBg);
  const [hi, lo] = a > b ? [a, b] : [b, a];
  return (hi + 0.05) / (lo + 0.05);
}

export function DesignPage() {
  const swatches: { name: string; token: string; hex: string; on: string; use: string }[] = [
    { name: "Ink 1", token: "--ink-1", hex: "#18181B", on: "#FFFFFF", use: "Headings, values, table cells" },
    { name: "Ink 2", token: "--ink-2", hex: "#3F3F46", on: "#FFFFFF", use: "Body copy, secondary values" },
    { name: "Ink 3", token: "--ink-3", hex: "#5F5F6B", on: "#FFFFFF", use: "Labels, captions, muted metadata" },
    { name: "Ink 4", token: "--ink-4", hex: "#A1A1AA", on: "#FFFFFF", use: "BORDERS ONLY — never text" },
    { name: "Accent", token: "--accent", hex: "#4338CA", on: "#FFFFFF", use: "Primary action, active nav, links" },
    { name: "Pass", token: "--pass", hex: "#15803D", on: "#F0FDF4", use: "PASS verdicts, healthy states" },
    { name: "Fail", token: "--fail", hex: "#B91C1C", on: "#FEF2F2", use: "FAIL verdicts, blocked sources" },
    { name: "Unknown", token: "--unknown", hex: "#A16207", on: "#FEFCE8", use: "UNKNOWN verdicts, warnings" },
    { name: "Waived", token: "--waived", hex: "#6D28D9", on: "#F5F3FF", use: "Exceptions, semantic authority, redactions" },
    { name: "Running", token: "--running", hex: "#1D4ED8", on: "#EFF6FF", use: "In-flight states" },
  ];

  return (
    <PageShell
      wide
      eyebrow="Reference"
        title="Design system"
        lede="Light mode only, self-contained, and scoped under a single class so it cannot be affected by the app's dark-first tokens. If this proposal is accepted, this page is the spec."
    >

      <Callout tone="accent" icon="eye" title="Legibility is a product requirement here, not a nicety.">
        This product's payload is a hash, a status literal and a timestamp. Every ratio in the table below is
        computed from the token's own hex value at render time, and every text colour clears WCAG AA for its
        size. <span className="vp-mono">--ink-4</span> is the one non-text grey — at 2.56:1 it is used only for
        borders and decorative icons, which is why it shows a ratio without a pass mark rather than a failure.
      </Callout>

      <Section
        title="Colour"
        note="Ten tokens. Every ratio below is COMPUTED from the hex values on render — none of them is a typed-in number that can drift from the palette."
      >
        <Card flush>
          <Table
            columns={[
              {
                key: "swatch",
                header: "",
                render: (s) => (
                  <span
                    style={{
                      display: "inline-block",
                      width: 34,
                      height: 22,
                      // radius-ignore: color swatch uses proportional data-viz softening below the app card scale
                      borderRadius: 5,
                      background: s.hex,
                      border: "1px solid var(--line)",
                    }}
                    aria-hidden="true"
                  />
                ),
              },
              { key: "name", header: "Name", primary: true, render: (s) => s.name },
              { key: "token", header: "Token", render: (s) => <span className="vp-mono">{s.token}</span> },
              { key: "hex", header: "Value", render: (s) => <span className="vp-mono">{s.hex}</span> },
              { key: "on", header: "On", render: (s) => <span className="vp-mono">{s.on}</span> },
              {
                key: "ratio",
                header: "Contrast",
                sortValue: (s) => contrast(s.hex, s.on),
                render: (s) => {
                  const r = contrast(s.hex, s.on);
                  // ink-4 is the one token that is SUPPOSED to fail as text — it
                  // is a border colour — so it reads "n/a (border)" rather than a
                  // red FAIL badge that implies a defect.
                  if (s.token === "--ink-4") {
                    return <Badge tone="neutral">{r.toFixed(2)}:1 · not text</Badge>;
                  }
                  return (
                    <Badge tone={r >= 4.5 ? "pass" : "fail"} glyph={r >= 4.5 ? "✓" : "✕"}>
                      {r.toFixed(2)}:1
                    </Badge>
                  );
                },
              },
              { key: "use", header: "Used for", render: (s) => <span style={{ color: "var(--ink-2)" }}>{s.use}</span> },
            ]}
            rows={swatches}
            rowKey={(s) => s.token}
          />
        </Card>
      </Section>

      <Section
        title="Verdicts"
        note="The four outcomes carry tone AND a distinct glyph, so they survive greyscale, printing and colour blindness. Colour alone never communicates a verdict."
      >
        <Card>
          <div className="vp-inline" style={{ gap: "calc(var(--spacing) * 3.5)" }}>
            <Verdict status="PASS" size="lg" />
            <Verdict status="FAIL" size="lg" />
            <Verdict status="UNKNOWN" size="lg" />
            <Verdict status="NOT_RUN" size="lg" />
          </div>
          <hr className="vp-divider" />
          <p style={{ margin: "0", fontSize: "var(--type-body-size)", lineHeight: "var(--type-prose-leading)", color: "var(--ink-2)" }}>
            <strong>UNKNOWN is amber, not a pale red.</strong> The domain model treats it as a real answer —
            "the evidence cannot decide" — never as a soft failure. A palette that shaded it toward FAIL would
            teach the wrong thing on sight, and the distinction between "this control is broken" and "we cannot
            see this control" is the most consequential one in the product.
          </p>
        </Card>
      </Section>

      <Section title="Severity is a separate axis" note="A critical finding and a FAIL check are different things and must not look alike.">
        <Card>
          <div className="vp-inline" style={{ gap: "calc(var(--spacing) * 4.5)" }}>
            <span className="vp-sev" data-sev="critical">
              <span className="vp-sev-dot" />
              critical
            </span>
            <span className="vp-sev" data-sev="high">
              <span className="vp-sev-dot" />
              high
            </span>
            <span className="vp-sev" data-sev="medium">
              <span className="vp-sev-dot" />
              medium
            </span>
            <span className="vp-sev" data-sev="low">
              <span className="vp-sev-dot" />
              low
            </span>
          </div>
          <hr className="vp-divider" />
          <p style={{ margin: "0", fontSize: "var(--type-body-size)", lineHeight: "var(--type-prose-leading)", color: "var(--ink-2)" }}>
            Severity describes how much a gap <em>matters</em>; a verdict describes what the engine{" "}
            <em>found</em>. Rendering both as filled red chips makes a critical UNKNOWN look like a failure and
            a low-severity FAIL look harmless. So severity is a dot plus a word — quieter by construction, and
            never confusable with a verdict badge.
          </p>
        </Card>
      </Section>

      <Section title="Buttons" note="Variant is emphasis, size is density. One primary per surface.">
        <Card>
          <div className="vp-btn-row" style={{ marginBottom: "calc(var(--spacing) * 3.5)" }}>
            <Button variant="primary" icon="check">
              Primary
            </Button>
            <Button variant="outline" icon="download">
              Outline
            </Button>
            <Button variant="ghost" icon="arrow-right">
              Ghost
            </Button>
            <Button variant="accept" icon="check">
              Accept
            </Button>
            <Button variant="reject" icon="x">
              Reject
            </Button>
            <Button variant="outline" disabled>
              Disabled
            </Button>
          </div>
          <div className="vp-btn-row">
            <Button variant="primary" size="sm">
              Small
            </Button>
            <Button variant="primary">Standard</Button>
            <Button variant="primary" size="lg">
              Large
            </Button>
          </div>
          <hr className="vp-divider" />
          <p style={{ margin: "0", fontSize: "var(--type-body-size)", lineHeight: "var(--type-prose-leading)", color: "var(--ink-2)" }}>
            <strong>accept</strong> and <strong>reject</strong> exist as their own variants because a decision
            pair is a distinct grammar from primary/secondary — the two options are equally available and the
            product must not visually nudge toward approving. Note that reject is an outlined red rather than a
            filled one: making rejection look more dangerous than acceptance would bias the review.
          </p>
        </Card>
      </Section>

      <Section title="Typography" note="One UI family, one mono. The mono is load-bearing.">
        <Card>
          <div style={{ display: "flex", flexDirection: "column", gap: "calc(var(--spacing) * 3)" }}>
            <div>
              <div className="vp-tiny">22px / 650 / -0.02em — page title</div>
              <div className="vp-h1">Multi-factor authentication is enforced</div>
            </div>
            <div>
              <div className="vp-tiny">14.5px / 650 — section heading</div>
              <div className="vp-h2">Check results</div>
            </div>
            <div>
              <div className="vp-tiny">14px / 1.68 — body prose, max 74ch</div>
              <div style={{ fontSize: "var(--type-body-size)", lineHeight: "var(--type-prose-leading)", color: "var(--ink-2)", maxWidth: "74ch" }}>
                A receipt is only as good as the things it pins. If a source changes tomorrow, the hashes in
                the receipt still name what was true at issue time.
              </div>
            </div>
            <div>
              <div className="vp-tiny">13px / 1.5 — table and row text</div>
              <div style={{ fontSize: "var(--type-body-size)", color: "var(--ink-2)" }}>
                2 of 412 active accounts have mfa_factor_count = 0
              </div>
            </div>
            <div>
              <div className="vp-tiny">12px mono, tabular — identifiers, hashes, literals</div>
              <div className="vp-inline">
                <span className="vp-mono">run_8818</span>
                <span className="vp-mono">pol_cc61_v3</span>
                <span className="vp-hash" style={{ cursor: "default" }}>
                  9f2c41ab7d…
                </span>
              </div>
            </div>
          </div>
          <hr className="vp-divider" />
          <p style={{ margin: "0", fontSize: "var(--type-body-size)", lineHeight: "var(--type-prose-leading)", color: "var(--ink-2)" }}>
            Hashes, ids and policy expressions are always monospace with{" "}
            <span className="vp-mono">tabular-nums</span>, because the only thing anyone ever does with a hash is
            compare it character by character against another one. Setting an identifier in a proportional face
            is a functional defect in this product, not a stylistic choice.
          </p>
        </Card>
      </Section>

      <Section title="Rules" note="Conventions this prototype holds itself to, and would ask the real system to hold.">
        <Card>
          <div className="vp-defs">
            {[
              {
                term: "Surfaces are cards",
                def: "One shadow, one border, one radius. Nested bordered divs inside a card are replaced by the list-row pattern.",
              },
              {
                term: "Worklists are tables; work is cards",
                def: "A table is for scanning many rows for a pattern. A card is for doing one thing. The same objects appear as both, in different places, on purpose.",
              },
              {
                term: "Every table header that looks sortable is sortable",
                def: "These tables are worklists — oldest-FAIL-first is a real need. A dead affordance is worse than none.",
              },
              {
                term: "Dates never show seconds",
                def: "Absolute times are UTC to the minute; relative times are computed against a frozen clock, so a screenshot taken today matches one taken next week.",
              },
              {
                term: "Required means a red dot, never the word",
                def: "Labels stay short and scannable. The help line below carries the reason, in one line.",
              },
              {
                term: "Empty states say what would fill them",
                def: "\"No findings match these filters\" plus the action that changes it — never a bare dash.",
              },
              {
                term: "Focus is visible on everything",
                def: "An audit surface is keyboard-driven for the people who live in it. A skip link precedes the eighteen-row rail, sortable headers are real buttons, and every row has a keyboard entry point.",
              },
              {
                term: "The prototype flag is always on screen",
                def: "In the top bar of every page, so no screenshot of any single screen can be mistaken for the shipped product.",
              },
            ].map((r) => (
              <div key={r.term} className="vp-def">
                <div className="vp-def-term">{r.term}</div>
                <div className="vp-def-body">{r.def}</div>
              </div>
            ))}
          </div>
        </Card>
      </Section>
    </PageShell>
  );
}
